/**
 * 서비스 상세 다이얼로그 컴포넌트
 *
 * 리팩토링: 599줄 → ~200줄
 * - PricingPlansSection: 요금제 플랜 표시
 * - ServiceAccessSection: 서비스 접근 및 연결
 * - MetadataSection: 추가 정보 표시
 *
 * 옛 구독 플랜 변경(크레딧 차감)은 구독 경로와 함께 지웠다 (#234).
 * 현재 플랜 · 체험판 표시는 서비스 메타데이터(subscription)로만 한다.
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import ConfirmDialog from '@/components/users/management/common/dialogs/ConfirmDialog';
import { getServiceStatusBadge, getHealthBadge } from '@/utils/badgeUtils';
import { MSAService } from '@/types';
import { adminAPI } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { PricingPlansSection } from './PricingPlansSection';
import { ServiceAccessSection } from './ServiceAccessSection';
import { MetadataSection } from './MetadataSection';
import { HealthStatus } from '@/types/health';

interface ServiceDetailDialogProps {
  service: MSAService | null;
  isOpen: boolean;
  onClose: () => void;
  healthStatuses: Record<number, HealthStatus>;
  isSubscribed?: boolean;
  onSubscribe?: (serviceSlug: string) => void;
  onUnsubscribe?: (serviceSlug: string) => void;
  onConnect?: (service: MSAService) => void;
}

export const ServiceDetailDialog: React.FC<ServiceDetailDialogProps> = ({
  service,
  isOpen,
  onClose,
  healthStatuses,
  isSubscribed = false,
  onSubscribe,
  onUnsubscribe,
  onConnect,
}) => {
  const { user } = useAuth();
  const [showUnsubscribeConfirm, setShowUnsubscribeConfirm] = useState(false);
  const [quotaPlans, setQuotaPlans] = useState<any[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  // 구독 상태
  const [subscribing, setSubscribing] = useState(false);
  const [subscribeError, setSubscribeError] = useState<string>('');
  const subscribingRef = useRef(false);

  const fetchQuotaPlans = useCallback(async () => {
    if (!service) return;
    setLoadingPlans(true);
    try {
      const plans = await adminAPI.getServiceQuotaPlans(service.service_slug, false);
      setQuotaPlans(plans || []);
    } catch (error) {
      console.error('플랜 정보를 불러오는데 실패했습니다:', error);
    } finally {
      setLoadingPlans(false);
    }
  }, [service]);

  // 현재 플랜 · 체험판 잔여 횟수는 서비스 메타데이터에서 읽는다 (#234 — 구독 조회 경로를 지웠다)
  const currentSubscription = useMemo(() => {
    if (!service || !isSubscribed) return null;
    const meta = (service.metadata?.subscription || {}) as {
      is_trial?: boolean;
      trial_remaining?: number;
      trial_remaining_uses?: number;
      subscription_plan_name?: string;
    };
    return {
      is_trial: meta.is_trial,
      plan_name: meta.subscription_plan_name,
      trial_remaining: meta.trial_remaining ?? meta.trial_remaining_uses,
      trial_remaining_uses: meta.trial_remaining_uses ?? meta.trial_remaining,
    };
  }, [service, isSubscribed]);

  useEffect(() => {
    if (isOpen && service) {
      fetchQuotaPlans();
    }
  }, [isOpen, service, fetchQuotaPlans]);

  if (!service) return null;

  const isDeleted = service.status === 'deleted';
  const isOnline = healthStatuses[service.id] === 'ONLINE';

  const handleUnsubscribe = async () => {
    try {
      await onUnsubscribe?.(service.service_slug);
      setShowUnsubscribeConfirm(false);
      onClose();
    } catch (error) {
      console.error('구독 해제 실패:', error);
    }
  };

  const handleConnect = async (svc: MSAService) => {
    await onConnect?.(svc);
  };

  return (
    <Dialog open={isOpen}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" onClose={onClose}>
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <DialogTitle className="text-2xl">{service.service_name}</DialogTitle>
                {service.version && (
                  <span className="text-sm text-gray-400 font-normal">v{service.version}</span>
                )}
                {isSubscribed && service.metadata?.subscription?.is_trial && (
                  <Badge variant="warning" size="sm">체험판</Badge>
                )}
              </div>
              {service.category && (
                <DialogDescription className="mt-2">{service.category}</DialogDescription>
              )}
            </div>
            <div className="flex flex-col items-end gap-2">
              {getServiceStatusBadge(service.status)}
              {service.status === 'approved' && getHealthBadge(healthStatuses[service.id])}
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* 서비스 설명 */}
          {service.service_description && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">서비스 설명</h3>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{service.service_description}</p>
            </div>
          )}

          {/* 서비스 접근 */}
          <ServiceAccessSection
            service={service}
            user={user}
            isSubscribed={isSubscribed}
            currentSubscription={currentSubscription}
            isDeleted={isDeleted}
            isOnline={isOnline}
            onConnect={handleConnect}
          />

          {/* 서비스 정보 */}
          <div className="grid grid-cols-2 gap-4">
            {service.contact_email && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-1">연락처 이메일</h3>
                <p className="text-sm text-gray-600">{service.contact_email}</p>
              </div>
            )}
            {service.contact_name && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-1">개발사</h3>
                <p className="text-sm text-gray-600">{service.contact_name}</p>
              </div>
            )}
          </div>

          {/* 요금제 플랜 */}
          <PricingPlansSection
            quotaPlans={quotaPlans}
            metadataPricingPlans={service.metadata?.pricing_plans}
            loading={loadingPlans}
          />

          {/* 추가 정보 */}
          <MetadataSection metadata={service.metadata} />

          {/* 구독 에러 메시지 */}
          {subscribeError && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-red-800">{subscribeError}</p>
            </div>
          )}

          {/* 액션 버튼 */}
          <DialogFooter>
            {isSubscribed ? (
              <>
                <Button variant="destructive" onClick={() => setShowUnsubscribeConfirm(true)}>
                  구독 해제
                </Button>
              </>
            ) : (
              <Button
                onClick={async () => {
                  if (subscribingRef.current) return;
                  subscribingRef.current = true;
                  setSubscribing(true);
                  setSubscribeError('');
                  try {
                    await onSubscribe?.(service.service_slug);
                    onClose();
                  } catch (error: any) {
                    console.error('구독 실패:', error);
                    const status = error?.response?.status;
                    const detail = error?.response?.data?.detail || error?.message;
                    if (status === 409) {
                      // 이미 구독 중 = 목표 달성이므로 에러 대신 정상 종료
                      onClose();
                    } else {
                      setSubscribeError(detail || '구독에 실패했습니다.');
                    }
                  } finally {
                    subscribingRef.current = false;
                    setSubscribing(false);
                  }
                }}
                disabled={isDeleted || subscribing}
              >
                {subscribing ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    구독 중...
                  </>
                ) : '구독하기'}
              </Button>
            )}
            <Button variant="outline" onClick={onClose}>
              닫기
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>

      <ConfirmDialog
        open={showUnsubscribeConfirm}
        title="서비스 구독 해제"
        description="정말로 이 서비스 구독을 해제하시겠습니까?"
        confirmText="구독 해제"
        cancelText="취소"
        variant="destructive"
        onConfirm={handleUnsubscribe}
        onCancel={() => setShowUnsubscribeConfirm(false)}
      />

    </Dialog>
  );
};

export default ServiceDetailDialog;
